import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Clock } from "lucide-react";
import { Event } from "@/hooks/useEvents";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

interface EventListItemProps {
  event: Event;
}

const EVENT_TYPE_LABELS: Record<Event['event_type'], string> = {
  ride: 'Ride',
  garage_day: 'Garage Day',
  cleanup: 'Cleanup',
  meetup: 'Meetup',
  training: 'Training',
  overlanding_trip: 'Overlanding Trip',
  competition: 'Competition',
};

const EVENT_TYPE_COLORS: Record<Event['event_type'], string> = {
  ride: 'bg-primary text-primary-foreground',
  garage_day: 'bg-orange-500 text-white',
  cleanup: 'bg-green-500 text-white',
  meetup: 'bg-blue-500 text-white',
  training: 'bg-purple-500 text-white',
  overlanding_trip: 'bg-amber-600 text-white',
  competition: 'bg-red-500 text-white',
};

export const EventListItem = ({ event }: EventListItemProps) => {
  const navigate = useNavigate();

  return (
    <div 
      className="flex items-start gap-4 p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/events/${event.id}`)}
    >
      <div className="flex-1">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-lg">{event.title}</h3>
            <div className="flex gap-2 mt-1">
              <Badge className={EVENT_TYPE_COLORS[event.event_type]}>
                {EVENT_TYPE_LABELS[event.event_type]}
              </Badge>
              {event.difficulty_estimate && (
                <Badge variant="outline">{event.difficulty_estimate}</Badge>
              )}
              {event.status !== 'upcoming' && (
                <Badge variant="outline">{event.status}</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground mb-2">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            {format(new Date(event.start_date), 'MMM d, yyyy')}
          </div>

          {event.meeting_point_description && (
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              <span className="truncate">{event.meeting_point_description}</span>
            </div>
          )}

          {event.estimated_duration_hours && (
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              {event.estimated_duration_hours}h
            </div>
          )}

          {event.max_participants && (
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Max {event.max_participants}
            </div>
          )}
        </div>

        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-1">
            {event.description}
          </p>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/events/${event.id}`);
        }}
      >
        View
      </Button>
    </div>
  );
};
